// SEO-coverage section: shows the full list of cities we serve and links
// to each city landing page (/bilforhandler-<slug>). Sits above the footer
// on the homepage to strengthen internal linking and local-keyword reach.
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { MapPin, ArrowRight, MoveRight, ChevronDown } from "lucide-react";
import { CITY_SEEDS } from "@/lib/cities";
import { CONTACT } from "@/config/contact";

// Mobile collapse threshold — keep the section compact on small screens
// while still surfacing the closest, highest-intent cities first.
const MOBILE_VISIBLE_COUNT = 8;

export function CityCoverageSection() {
  // Sort by distance so closest cities surface first — matches buyer intent
  // and keeps the most-relevant local pages in the prime above-the-fold band.
  const sorted = [...CITY_SEEDS].sort((a, b) => a.distanceKm - b.distanceKm);
  const [showAllMobile, setShowAllMobile] = useState(false);
  const hasOverflow = sorted.length > MOBILE_VISIBLE_COUNT;

  return (
    <section className="relative overflow-hidden bg-background py-20 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-border before:to-transparent">
      {/* Decorative Denmark silhouette — subtle accent tint on light bg */}
      <svg
        aria-hidden="true"
        viewBox="0 0 600 700"
        className="pointer-events-none absolute -right-20 top-1/2 hidden h-[120%] -translate-y-1/2 text-accent/[0.08] md:block"
        fill="currentColor"
      >
        <path d="M300 50c-40 20-60 60-80 100s-30 70-50 90-40 30-50 60 0 60 20 80 40 30 50 60-10 70-30 90-30 50-20 80 30 50 60 60 60-10 90-20 60-20 90-10 50 30 80 40 60-10 70-40 0-60-20-80-40-30-50-60 10-60 30-80 40-30 30-60-30-50-60-60-50-30-50-60 20-50 30-80-10-60-30-80-40-30-60-50-50-30-80-30z" />
      </svg>

      <div className="relative z-10 mx-auto max-w-7xl px-6">
        <div className="max-w-2xl">
          <span className="text-sm font-semibold uppercase tracking-widest text-accent">
            Vi dækker hele Trekantområdet
          </span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Bilforhandler du kan stole på — også i din by
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Selvom vi holder til i Børkop, kommer der hver uge kunder fra hele
            Syd- og Sønderjylland samt Fyn. Vælg din by herunder og se hvorfor
            naboer vælger AutoXChange, når de skal købe brugt bil.
          </p>
        </div>

        <h3 className="mt-12 flex items-center gap-2 text-lg font-semibold tracking-tight text-foreground">
          <MoveRight
            size={22}
            className="text-accent md:size-[18px]"
            aria-hidden="true"
          />
          Find os i din by
        </h3>

        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {sorted.map((city, index) => {
            // On mobile only, hide cities past the threshold until expanded.
            // From `sm:` upward always show everything — desktop has space.
            const hiddenOnMobile =
              !showAllMobile && index >= MOBILE_VISIBLE_COUNT;
            return (
              <Link
                key={city.slug}
                to="/bilforhandler-{$citySlug}"
                params={{ citySlug: city.slug }}
                className={`group flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground/70 transition-all hover:bg-accent/5 hover:text-accent ${
                  hiddenOnMobile ? "hidden sm:flex" : ""
                }`}
              >
                <MapPin
                  size={14}
                  className="shrink-0 text-accent/70 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-accent motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
                />
                <span className="relative font-medium after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:origin-bottom-right after:scale-x-0 after:bg-accent after:transition-transform after:duration-300 group-hover:after:origin-bottom-left group-hover:after:scale-x-100 motion-reduce:after:transition-none">
                  Bilforhandler {city.name}
                </span>
              </Link>
            );
          })}
        </div>

        {hasOverflow && (
          <div className="mt-4 flex justify-center sm:hidden">
            <button
              type="button"
              onClick={() => setShowAllMobile((prev) => !prev)}
              aria-expanded={showAllMobile}
              className="group inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground/80 transition-all hover:border-accent hover:text-accent"
            >
              {showAllMobile
                ? "Vis færre byer"
                : `Vis alle ${sorted.length} byer`}
              <ChevronDown
                size={16}
                className={`text-accent transition-transform duration-200 motion-reduce:transition-none ${
                  showAllMobile ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              />
            </button>
          </div>
        )}

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-8">
          <p className="text-sm text-muted-foreground">
            Kan du ikke se din by? Vi leverer i hele Danmark — kontakt os for et
            uforpligtende tilbud.
          </p>
          <Link
            to="/kontakt"
            className="group inline-flex items-center gap-2 rounded-lg bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground transition-all hover:brightness-110"
          >
            Kontakt os
            <ArrowRight
              size={14}
              className="transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
            />
          </Link>
        </div>

        <p className="mt-6 text-xs text-muted-foreground/70">
          Forhandler-adresse: {CONTACT.addressLine}
        </p>
      </div>
    </section>
  );
}
